import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { readConsent, writeConsent, hasGlobalPrivacyControl } from "@/lib/consent";
import { Cookie } from "lucide-react";
import { isLandingPath } from "@/lib/landingPaths";

const CookieConsent = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [replay, setReplay] = useState(true);

  const isAdmin = location.pathname.startsWith("/112076");
  const isPrivacyChoices = location.pathname === "/your-privacy-choices";
  const isLanding = isLandingPath(location.pathname);

  useEffect(() => {
    if (isAdmin || isPrivacyChoices || isLanding) return;
    const stored = readConsent();
    if (!stored && !hasGlobalPrivacyControl()) {
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, [isAdmin, isPrivacyChoices, isLanding]);

  if (isAdmin || isPrivacyChoices || isLanding || !visible) return null;

  const acceptAll = () => {
    writeConsent({ analytics: true, replay: true, ccpa_opt_out: false });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ analytics: false, replay: false, ccpa_opt_out: true });
    setVisible(false);
  };
  const savePrefs = () => {
    writeConsent({ analytics, replay, ccpa_opt_out: !analytics && !replay });
    setPrefsOpen(false);
    setVisible(false);
  };

  return (
    <>
      <div
        role="dialog"
        aria-label="Cookie consent"
        className="fixed bottom-0 inset-x-0 z-[60] px-2 pb-2 sm:px-4 sm:pb-4"
      >
        <div className="mx-auto max-w-5xl rounded-lg sm:rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-2xl p-3 sm:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="flex items-start gap-2.5 sm:gap-3 flex-1">
              <div className="shrink-0 mt-0.5 rounded-full bg-primary/10 p-1.5 sm:p-2">
                <Cookie className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground leading-snug sm:leading-relaxed">
                <p className="text-foreground font-medium mb-0.5 sm:mb-1 text-sm">We value your privacy</p>
                <p>
                  We use cookies to analyze site traffic and improve your experience. See our{" "}
                  <Link to="/privacy-policy" className="text-primary underline underline-offset-2">Privacy Policy</Link>
                  {" "}and{" "}
                  <Link to="/your-privacy-choices" className="text-primary underline underline-offset-2">Your Privacy Choices</Link>.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 md:flex md:flex-wrap gap-2 md:justify-end">
              <Button variant="outline" size="sm" onClick={() => setPrefsOpen(true)} className="h-9 px-2 text-xs sm:text-sm sm:px-3">
                Manage
              </Button>
              <Button variant="outline" size="sm" onClick={rejectAll} className="h-9 px-2 text-xs sm:text-sm sm:px-3">
                Necessary Only
              </Button>
              <Button size="sm" onClick={acceptAll} className="h-9 px-2 text-xs sm:text-sm sm:px-3 bg-primary hover:bg-primary/90 text-primary-foreground">
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which categories of cookies to allow. Necessary cookies are always active.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium">Strictly Necessary</p>
                <p className="text-xs text-muted-foreground">Required for the site to function. Always on.</p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium">Analytics</p>
                <p className="text-xs text-muted-foreground">Google Analytics and Microsoft Clarity.</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium">Session Replay</p>
                <p className="text-xs text-muted-foreground">Contentsquare session recording.</p>
              </div>
              <Switch checked={replay} onCheckedChange={setReplay} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrefsOpen(false)}>Cancel</Button>
            <Button onClick={savePrefs} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
